/**
 * @component
 * @memberof module:CoreModule
 * @category Components
 */

import { Component, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

/**
 * A required super type the inherited child must return.
 *
 * Don't export it. Only this class should be able to use it.
 *
 * @class
 * @see [super.onDestroy() in derived component](https://stackoverflow.com/a/54985080)
 * @see [A class that automatically unsubscribes all observables](https://stackoverflow.com/a/57146137)
 */
export class REQUIRED_SUPER { }

/**
 * Abstract base component for handling HTTP operations and managing subscriptions.
 *
 * This component provides common functionality for derived components, including
 * loading state management and subscription handling.
 *
 * @abstract
 * @component
 */
@Component({ template: '' })
export abstract class BaseHttpComponent implements OnDestroy {

	/**
	 * Manages active subscriptions to observables to prevent memory leaks.
	 *
	 * @type {Subscription}
	 * @private
	 * @hidden
	 */
	private subscriptions: Subscription;

	/**
	 * BehaviorSubject that indicates the loading state of the component.
	 *
	 * This subject allows components to reactively respond to loading state changes.
	 * Primitive datatype is immutable, so its state change is not recognized in a template.
	 * Tell Angular to subscribe to it until the root variable to be checked.
	 *
	 * @public
	 * @type {BehaviorSubject<boolean>}
	 *
	 * @see [Angular Change Detection Explained](https://blog.thoughtram.io/angular/2016/02/22/angular-2-change-detection-explained.html)
	 * @see [ChangeDetectionStrategy.OnPush explained](https://stackoverflow.com/a/48605880)
	 */
	public isLoading$: BehaviorSubject<boolean>;

	/**
	 * Gets the current loading state.
	 *
	 * @getter
	 * @returns {boolean} The current loading state.
	 */
	get isLoading(): boolean { return this.isLoading$?.value; }

	/**
	 * Sets the loading state and notifies subscribers.
	 *
	 * @setter
	 * @param {boolean} isLoading - The new loading state to set.
	 */
	set isLoading(isLoading: boolean) { this.isLoading$.next(isLoading); }

	/**
	 * Initializes the base HTTP component, setting up the subscriptions and loading state.
	 *
	 * @constructor
	 * @protected
	 */
	protected constructor() {
		this.subscriptions = new Subscription();
		this.isLoading$ = new BehaviorSubject<boolean>(false);

		// This method does not work for some reason
		// let f = this.ngOnDestroy;
		// this.ngOnDestroy = () => {
		//   f();
		//   this.unsubscribeAll();
		// };
	}

	/**
	 * Safely collect the subscriptions/
	 *
	 * @param {Subscription} sub - The subscription to add.
	 * @returns {Subscription}
	 * @protected
	 *
	 * @see [Class inheritance and Angular components lifecycle](https://stacksandfoundations.wordpress.com/2016/06/24/using-class-inheritance-to-hook-to-angular2-component-lifecycle/)
	 */
	protected safeSubscription(sub: Subscription): Subscription {
		if (! sub.closed) this.subscriptions.add(sub);
		return sub;
	}

	/**
	 * Safely unsubscribe from the collected subscriptions.
	 *
	 * @private
	 * @hidden
	 */
	private unsubscribeAll() {
		this.subscriptions.unsubscribe();
	}

	/**
	 * Cleans up subscriptions when the component is destroyed to prevent memory leaks.
	 *
	 * @override
	 */
	ngOnDestroy(): REQUIRED_SUPER {
		this.unsubscribeAll();
		return new REQUIRED_SUPER();
	}
}
